import {
  getCurrentRange,
  showSlashCommandMenu,
} from "@circle-react-shared/uikit/TipTapBlockEditor/Toolbar/helpers";
import { EmojiPickerV4 } from "@circle-react-uikit/EmojiPickerV4";
import { Portal } from "@circle-react-uikit/Portal";
import { IconButton } from "@circleco/compass/components/IconButton";
import { Tooltip } from "@circleco/compass/components/Tooltip";
import type { Editor } from "@tiptap/react";

import { t } from "@/i18n-js/instance";

import type { ToolbarButton } from "./types";

interface CommonHandlerProps {
  editor: Editor;
  range: {
    from: number;
    to: number;
  };
}

interface ToolbarCompassProps {
  disabledExtensions: Record<string, boolean>;
  editor: Editor;
  hiddenButtons?: ToolbarButton[];
  portalId: string;
  showFileUploadModal: (
    props: CommonHandlerProps & {
      type?: "file" | "video" | "audio";
    },
  ) => void;
  showGiphyPickerModal: (props: CommonHandlerProps) => void;
  showImagePickerModal: (props: CommonHandlerProps) => void;
  showPollModal: (props: CommonHandlerProps) => void;
  showVideoClipRecorder: (props: CommonHandlerProps) => void;
  showVoiceMessageModal: (props: CommonHandlerProps) => void;
}

export const ToolbarCompass = ({
  editor,
  portalId,
  showImagePickerModal,
  showFileUploadModal,
  showGiphyPickerModal,
  showPollModal,
  disabledExtensions,
  showVideoClipRecorder,
  showVoiceMessageModal,
  hiddenButtons = [],
}: ToolbarCompassProps) => {
  if (!editor || !portalId) return null;
  return (
    <Portal selectorQuery={`#${portalId}`}>
      <ul className="flex list-none items-center">
        {!hiddenButtons.includes("slash_commands") && (
          <li>
            <Tooltip content={t("tiptap.open_slash_commands_menu")}>
              <IconButton
                variant="ghost"
                size="md"
                icon="circle-plus"
                aria-label={t("tiptap.open_slash_commands_menu")}
                data-testid="open_slash_commands_menu"
                onClick={() => showSlashCommandMenu({ editor })}
              />
            </Tooltip>
          </li>
        )}
        {!disabledExtensions["entities"] &&
          !hiddenButtons.includes("entity") && (
            <li>
              <Tooltip content={t("tiptap.add_entity")}>
                <IconButton
                  variant="ghost"
                  size="md"
                  icon="hashtag"
                  aria-label={t("tiptap.add_entity")}
                  data-testid="add_entity_list"
                  onClick={() => {
                    editor.chain().focus().insertContent(" #").run();
                  }}
                />
              </Tooltip>
            </li>
          )}
        {!disabledExtensions["file_loader"] &&
          !hiddenButtons.includes("file") && (
            <li className="hidden md:flex">
              <Tooltip content={t("tiptap.attach_file")}>
                <IconButton
                  variant="ghost"
                  size="md"
                  icon="paperclip"
                  aria-label={t("tiptap.attach_file")}
                  data-testid="attach_file"
                  onClick={() =>
                    showFileUploadModal({
                      editor,
                      range: getCurrentRange({ editor }),
                    })
                  }
                />
              </Tooltip>
            </li>
          )}
        {!disabledExtensions["file_loader"] &&
          !hiddenButtons.includes("video") && (
            <li>
              <Tooltip content={t("tiptap.attach_video")}>
                <IconButton
                  variant="ghost"
                  size="md"
                  icon="square-play"
                  aria-label={t("tiptap.attach_video")}
                  data-testid="attach_video"
                  onClick={() =>
                    showFileUploadModal({
                      editor,
                      range: getCurrentRange({ editor }),
                      type: "video",
                    })
                  }
                />
              </Tooltip>
            </li>
          )}
        {!disabledExtensions["file_loader"] &&
          !hiddenButtons.includes("gif") && (
            <li className="hidden md:flex">
              <Tooltip content={t("tiptap.add_gif")}>
                <IconButton
                  variant="ghost"
                  size="md"
                  icon="gif-square"
                  aria-label={t("tiptap.add_gif")}
                  data-testid="add_gif"
                  onClick={() =>
                    showGiphyPickerModal({
                      editor,
                      range: getCurrentRange({ editor }),
                    })
                  }
                />
              </Tooltip>
            </li>
          )}
        {!disabledExtensions["image"] && !hiddenButtons.includes("image") && (
          <li>
            <Tooltip content={t("tiptap.add_image")}>
              <IconButton
                variant="ghost"
                size="md"
                icon="image"
                aria-label={t("tiptap.add_image")}
                data-testid="add_image"
                onClick={() =>
                  showImagePickerModal({
                    editor,
                    range: getCurrentRange({ editor }),
                  })
                }
              />
            </Tooltip>
          </li>
        )}
        {!disabledExtensions["emojipicker"] &&
          !hiddenButtons.includes("emoji") && (
            <li>
              <EmojiPickerV4
                onEmojiSelect={emojiData => {
                  if (!emojiData.emoji) return;
                  editor.chain().focus().insertContent(emojiData.emoji).run();
                }}
                targetWrapperClassName="!flex"
                target={
                  <Tooltip content={t("tiptap.add_emoji")}>
                    <IconButton
                      variant="ghost"
                      size="md"
                      icon="emoji-smiley"
                      aria-label={t("tiptap.add_emoji")}
                      data-testid="add_emoji"
                    />
                  </Tooltip>
                }
              />
            </li>
          )}
        {!disabledExtensions["poll"] && !hiddenButtons.includes("poll") && (
          <li>
            <Tooltip content={t("tiptap.add_poll")}>
              <IconButton
                variant="ghost"
                size="md"
                icon="checklist"
                aria-label={t("tiptap.add_poll")}
                data-testid="add_poll"
                onClick={() =>
                  showPollModal({
                    editor,
                    range: getCurrentRange({ editor }),
                  })
                }
              />
            </Tooltip>
          </li>
        )}
        {!disabledExtensions["voice_message"] &&
          !hiddenButtons.includes("voice_message") && (
            <li>
              <Tooltip content={t("tiptap.record_voice_message")}>
                <IconButton
                  variant="ghost"
                  size="md"
                  icon="microphone"
                  aria-label={t("tiptap.record_voice_message")}
                  data-testid="record_voice_message"
                  onClick={() =>
                    showVoiceMessageModal({
                      editor,
                      range: getCurrentRange({ editor }),
                    })
                  }
                />
              </Tooltip>
            </li>
          )}
        {!disabledExtensions["video_clip"] &&
          !hiddenButtons.includes("video_clip") && (
            <li>
              <Tooltip content={t("tiptap.record_video_clip")}>
                <IconButton
                  variant="ghost"
                  size="md"
                  icon="video"
                  aria-label={t("tiptap.record_video_clip")}
                  data-testid="record_video_clip_toolbar"
                  onClick={() =>
                    showVideoClipRecorder({
                      editor,
                      range: getCurrentRange({ editor }),
                    })
                  }
                />
              </Tooltip>
            </li>
          )}
      </ul>
    </Portal>
  );
};
